// src/components/black-friday/campaign-types-view.tsx
import React, { useState } from "react";
import { BFChannelData } from "@/types/black-friday";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface Props {
  channelData25: BFChannelData[];
  channelData24: BFChannelData[];
  channelData23: BFChannelData[];
  comparisonPeriod: 'year2024_total' | 'year2024_same' | 'year2023_total' | 'year2023_same';
}

type ColorScheme = 'standard' | 'accessible';

// Color schemes for campaign types - maximally distinct colors
const COLOR_SCHEMES = {
  standard: {
    'Performance Max': '#8b5cf6',  // Violet
    'Search': '#f97316',           // Orange (very different from violet)
    'Shopping': '#10b981',         // Emerald Green (very different from orange)
    'Display': '#0ea5e9',          // Sky Blue (very different from green)
    'Video': '#f43f5e',            // Rose Red (very different from blue)
    'Demand Gen': '#eab308',       // Yellow (very different from red)
  },
  accessible: {
    'Performance Max': '#E89C6C',  // Coral/Orange
    'Search': '#D4B75F',           // Golden Yellow
    'Shopping': '#3C9C8F',         // Teal/Cyan
    'Display': '#5FB3D4',          // Sky Blue
    'Video': '#2C3E50',            // Dark Navy
    'Demand Gen': '#9CA3AF',       // Light Grey
  },
};

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const daysText = label > 0 ? `BF +${label}` : label === 0 ? "Black Friday" : `BF ${label}`;

    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold mb-2">{daysText}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: ${entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function CampaignTypesView({ channelData25, channelData24, channelData23, comparisonPeriod }: Props) {
  const [colorScheme, setColorScheme] = useState<ColorScheme>('standard');

  const colors = COLOR_SCHEMES[colorScheme];

  // Calculate days until Black Friday 2025 (Nov 28, 2025)
  const today = new Date();
  const blackFriday2025 = new Date('2025-11-28');
  const daysUntilBF = Math.ceil((blackFriday2025.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const maxIndex2025 = -Math.abs(daysUntilBF); // e.g., -34 if 34 days before BF

  // Prepare channel spend data for 2025 - full range but only show data up to today
  // Create full range from -70 to +14 to show complete x-axis
  const fullRangeData: any[] = [];
  for (let i = -70; i <= 14; i++) {
    const dataRow = channelData25.find((row) => row.index === i);
    fullRangeData.push({
      index: i,
      'Performance Max': (i <= maxIndex2025 && dataRow) ? (dataRow['PERFORMANCE_MAX Cost'] || 0) : 0,
      'Search': (i <= maxIndex2025 && dataRow) ? (dataRow['SEARCH Cost'] || 0) : 0,
      'Shopping': (i <= maxIndex2025 && dataRow) ? (dataRow['SHOPPING Cost'] || 0) : 0,
      'Display': (i <= maxIndex2025 && dataRow) ? (dataRow['DISPLAY Cost'] || 0) : 0,
      'Video': (i <= maxIndex2025 && dataRow) ? (dataRow['VIDEO Cost'] || 0) : 0,
      'Demand Gen': (i <= maxIndex2025 && dataRow) ? (dataRow['DEMAND_GEN Cost'] || 0) : 0,
    });
  }
  const channelSpendData = fullRangeData;

  // For comparison chart - select the correct year's data and show all data if "total" period
  const comparisonChannelSource = comparisonPeriod.startsWith('year2024') ? channelData24 : channelData23;
  const showFullComparisonPeriod = comparisonPeriod === 'year2024_total' || comparisonPeriod === 'year2023_total';

  // Create comparison data with full range or filtered
  const comparisonFullRangeData: any[] = [];
  for (let i = -70; i <= 14; i++) {
    const dataRow = comparisonChannelSource.find((row) => row.index === i);
    const shouldShowData = showFullComparisonPeriod || i <= maxIndex2025;
    comparisonFullRangeData.push({
      index: i,
      'Performance Max': (shouldShowData && dataRow) ? (dataRow['PERFORMANCE_MAX Cost'] || 0) : 0,
      'Search': (shouldShowData && dataRow) ? (dataRow['SEARCH Cost'] || 0) : 0,
      'Shopping': (shouldShowData && dataRow) ? (dataRow['SHOPPING Cost'] || 0) : 0,
      'Display': (shouldShowData && dataRow) ? (dataRow['DISPLAY Cost'] || 0) : 0,
      'Video': (shouldShowData && dataRow) ? (dataRow['VIDEO Cost'] || 0) : 0,
      'Demand Gen': (shouldShowData && dataRow) ? (dataRow['DEMAND_GEN Cost'] || 0) : 0,
    });
  }
  const comparisonChannelData = comparisonFullRangeData;

  const comparisonLabel =
    comparisonPeriod === 'year2024_total' ? '2024 (Total)' :
    comparisonPeriod === 'year2024_same' ? '2024 (Same Period)' :
    comparisonPeriod === 'year2023_total' ? '2023 (Total)' :
    '2023 (Same Period)';

  return (
    <div className="space-y-6">
      {/* 2025 Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Campaign Types - BF 2025</CardTitle>
              <CardDescription>
                Daily spend breakdown by campaign type during the Black Friday period (10 weeks before to 2 weeks after)
              </CardDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant={colorScheme === 'standard' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setColorScheme('standard')}
              >
                Standard
              </Button>
              <Button
                variant={colorScheme === 'accessible' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setColorScheme('accessible')}
              >
                Accessible
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={channelSpendData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="index"
                label={{ value: "Days from Black Friday", position: "insideBottom", offset: -5 }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine
                x={0}
                stroke="hsl(var(--destructive))"
                strokeDasharray="3 3"
              />
              <Bar dataKey="Performance Max" stackId="a" fill={colors['Performance Max']} />
              <Bar dataKey="Search" stackId="a" fill={colors['Search']} />
              <Bar dataKey="Shopping" stackId="a" fill={colors['Shopping']} />
              <Bar dataKey="Display" stackId="a" fill={colors['Display']} />
              <Bar dataKey="Video" stackId="a" fill={colors['Video']} />
              <Bar dataKey="Demand Gen" stackId="a" fill={colors['Demand Gen']} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Types - {comparisonLabel}</CardTitle>
          <CardDescription>
            Campaign type spend breakdown for the comparison period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={comparisonChannelData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="index"
                label={{ value: "Days from Black Friday", position: "insideBottom", offset: -5 }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine
                x={0}
                stroke="hsl(var(--destructive))"
                strokeDasharray="3 3"
              />
              <Bar dataKey="Performance Max" stackId="a" fill={colors['Performance Max']} />
              <Bar dataKey="Search" stackId="a" fill={colors['Search']} />
              <Bar dataKey="Shopping" stackId="a" fill={colors['Shopping']} />
              <Bar dataKey="Display" stackId="a" fill={colors['Display']} />
              <Bar dataKey="Video" stackId="a" fill={colors['Video']} />
              <Bar dataKey="Demand Gen" stackId="a" fill={colors['Demand Gen']} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
